import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
  FormHelperText,
  Alert,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  CloudUpload,
  MiscellaneousServices,
  Delete,
} from "@mui/icons-material";
import {
  createServiceOption,
  updateServiceOption,
  getServiceOptionById,
  getServiceSubCategories,
} from "../../../services/Api";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const CreateAndModifyServiceOptions = () => {
  const { subServiceId: routeSubServiceId, optionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = Boolean(optionId);

  // Get subServiceId from query params for edit mode, or from route params for create mode
  const subServiceId = isEdit
    ? searchParams.get("subServiceId") || routeSubServiceId
    : routeSubServiceId;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [nameError, setNameError] = useState("");
  const [subCategoryError, setSubCategoryError] = useState("");
  const [priceError, setPriceError] = useState("");
  const [imageError, setImageError] = useState("");
  const [subCategories, setSubCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    subCategoryId: subServiceId || "",
    price: "",
    duration: "",
    discountPrice: "",
    description: "",
    isActive: true,
    optionImages: null,
  });

  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (isEdit && optionId) {
      fetchServiceOption();
    }
    // Fetch sub-categories for dropdown
    fetchSubCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, optionId]);

  const fetchServiceOption = async () => {
    try {
      setInitialLoading(true);
      const response = await getServiceOptionById(optionId);
      if (response.success) {
        const option = response.data;
        setFormData({
          name: option.name || "",
          subCategoryId: option.subCategoryId || subServiceId || "",
          price: option.price ? Math.round(Number(option.price)) : "",
          duration: option.duration ? Math.round(Number(option.duration)) : "",
          discountPrice: option.discountPrice
            ? Math.round(Number(option.discountPrice))
            : "",
          description: option.description || "",
          isActive: option.isActive !== undefined ? option.isActive : true,
          optionImages: null,
        });
        setImagePreview(option.image || option.optionImages || "");
      }
    } catch (err) {
      console.error("Error fetching service option:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch service option details");
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await getServiceSubCategories();
      if (response.success) {
        setSubCategories(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching sub categories:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let finalValue = value;
    if (name === "price" || name === "discountPrice" || name === "duration") {
      if (finalValue !== "") {
        finalValue = finalValue.replace(/\D/g, "");
      }
    }

    if (name === "name") {
      // const hasInvalid = /[^a-zA-Z\s]/.test(value);
      // if (hasInvalid) {
      //   setNameError(
      //     "Service Option Name cannot contain special characters or numbers",
      //   );
      //   return;
      // }
      setNameError("");
    } else if (name === "price") {
      setPriceError("");
    } else if (name === "subCategoryId") {
      setSubCategoryError("");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : finalValue,
    }));
  };

  const handleQuillChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        optionImages: file,
      }));
      setImageError("");

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setError(null);
    let hasErrors = false;

    // Validation
    if (!formData.subCategoryId) {
      setSubCategoryError("Sub Category is required");
      hasErrors = true;
    } else {
      setSubCategoryError("");
    }

    if (!formData.price || formData.price <= 0) {
      setPriceError("Price must be greater than 0");
      hasErrors = true;
    } else {
      setPriceError("");
    }

    if (!isEdit && !formData.optionImages) {
      setImageError("Service Option Image is required");
      hasErrors = true;
    } else {
      setImageError("");
    }

    if (hasErrors) return;

    try {
      setLoading(true);

      const submitData = new FormData();
      submitData.append("name", formData.name.trim());
      submitData.append("subCategoryId", formData.subCategoryId);
      submitData.append("price", formData.price);
      submitData.append("duration", formData.duration || 0);
      submitData.append("discountPrice", formData.discountPrice || 0);
      submitData.append("description", formData.description || "");

      if (isEdit) {
        submitData.append("isActive", formData.isActive);
      }

      if (formData.optionImages) {
        submitData.append("optionImages", formData.optionImages);
      }

      let response;
      if (isEdit) {
        response = await updateServiceOption(optionId, submitData);
      } else {
        response = await createServiceOption(submitData);
      }

      console.log(response, "response");

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/services/service-options/${subServiceId}`);
        }, 1500);
      } else {
        setError(
          response.message ||
            `Failed to ${isEdit ? "update" : "create"} service option`,
        );
      }
    } catch (err) {
      console.error("Error saving service option:", err);

      console.log(err.response.data.message, "errpr");
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `An error occurred while ${isEdit ? "updating" : "creating"} the service option`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() =>
              navigate(`/services/service-options/${subServiceId}`)
            }
            sx={{ color: "primary.main" }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 300, color: "text.primary" }}
            >
              {isEdit ? "Edit Service Option" : "Create Service Option"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? "Update service option information"
                : "Add a new service option to the system"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "text.border",
          borderRadius: 2,
          p: 4,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Service Option {isEdit ? "updated" : "created"} successfully!
            Redirecting...
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {/* Left Column */}
            <Box>
              {/* Name Field */}
              <TextField
                fullWidth
                size="small"
                required
                label="Service Option Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(nameError)}
                helperText={nameError}
                sx={{ mb: 3 }}
              />

              {/* Price Field */}
              <TextField
                fullWidth
                size="small"
                label="Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                required
                error={Boolean(priceError)}
                helperText={priceError}
                inputProps={{ min: 0 }}
                sx={{ mb: 3 }}
              />

              {/* Duration Field */}
              <TextField
                fullWidth
                size="small"
                label="Duration (minutes)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                inputProps={{ min: 0 }}
                sx={{ mb: 3 }}
              />

              {/* Description Field */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  Description
                </Typography>
                <Box
                  sx={{
                    "& .ql-container": {
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                      minHeight: "150px",
                    },
                    "& .ql-toolbar": {
                      borderTopLeftRadius: "8px",
                      borderTopRightRadius: "8px",
                      backgroundColor: "rgba(0,0,0,0.02)",
                    },
                    "& .ql-editor": { fontSize: "0.9rem" },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(val) => handleQuillChange("description", val)}
                    placeholder="Detailed description of the service option..."
                  />
                </Box>
              </Box>
            </Box>

            {/* Right Column */}
            <Box>
              {/* Sub Category Field */}
              <FormControl
                fullWidth
                size="small"
                sx={{ mb: 3 }}
                error={Boolean(subCategoryError)}
              >
                <InputLabel shrink>Sub Category</InputLabel>
                <Select
                  name="subCategoryId"
                  value={formData.subCategoryId}
                  onChange={handleInputChange}
                  label="Sub Category"
                  required
                  size="small"
                >
                  <MenuItem value="">Select Sub Category</MenuItem>
                  {subCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {subCategoryError && (
                  <FormHelperText>{subCategoryError}</FormHelperText>
                )}
              </FormControl>

              {/* Discount Price Field */}
              <TextField
                fullWidth
                size="small"
                label="Discount Price"
                name="discountPrice"
                type="number"
                value={formData.discountPrice}
                onChange={handleInputChange}
                inputProps={{ min: 0 }}
                sx={{ mb: 3 }}
              />

              {/* Status Toggle (only for edit) */}
              {isEdit && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Active Status"
                  sx={{ mb: 3 }}
                />
              )}

              {/* Image Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Service Option Image
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      "&:hover .delete-overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={imagePreview}
                      sx={{
                        width: 100,
                        height: 100,
                        border: "1px solid",
                        borderColor: "text.border",
                      }}
                    >
                      <MiscellaneousServices sx={{ fontSize: 40 }} />
                    </Avatar>
                    {imagePreview && (
                      <Box
                        className="delete-overlay"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          borderRadius: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setImagePreview("");
                          setFormData((prev) => ({
                            ...prev,
                            optionImages: null,
                          }));
                        }}
                      >
                        <Delete sx={{ color: "white" }} />
                      </Box>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{ textTransform: "none" }}
                  >
                    Upload Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                </Box>
                {imageError && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {imageError}
                  </FormHelperText>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Recommended size: 300x300px. Max size: 5MB.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}
          >
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/services/service-options/${subServiceId}`)
              }
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{
                textTransform: "none",
                backgroundColor: "primary.main",
                minWidth: 120,
              }}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateAndModifyServiceOptions;
